import { supabase } from "./churchSupabaseClient";
import { fetchOrganizationAccess } from "./churchOrganizationAccessService";

const ANNOUNCEMENTS_KEY = "oikos.church.announcements";
const EVENTS_KEY = "oikos.church.events";
const CHURCH_ANNOUNCEMENTS_TABLE = "church_announcements";
const CHURCH_EVENTS_TABLE = "church_events";
const CHURCH_FAMILIES_TABLE = "church_families";
const CHURCH_MEMBERS_TABLE = "church_members";
const MAX_PRE_SERVICE_ENTRIES = 6;
const MISSING_TABLES_KEY = "oikos.church.missingTables";
const MISSING_TABLE_RETRY_MS = 5 * 60 * 1000;

function isMissingRelationError(error) {
  const message = String(error?.message || "").toLowerCase();
  return (
    error?.code === "42P01" ||
    error?.code === "PGRST205" ||
    message.includes("could not find the table") ||
    message.includes("schema cache")
  );
}

function isMissingColumnError(error, columnName) {
  const message = String(error?.message || "").toLowerCase();
  return (
    error?.code === "42703" ||
    error?.code === "PGRST204" ||
    (message.includes(columnName.toLowerCase()) && message.includes("column"))
  );
}

function getMissingTables() {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const parsed = JSON.parse(window.localStorage.getItem(MISSING_TABLES_KEY) || "{}");
    if (Array.isArray(parsed)) {
      return Object.fromEntries(parsed.map((tableName) => [tableName, Date.now()]));
    }
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    return {};
  }
}

function isTableMarkedMissing(tableName) {
  const missingTables = getMissingTables();
  const markedAt = Number(missingTables[tableName] || 0);
  if (!markedAt) return false;
  return Date.now() - markedAt < MISSING_TABLE_RETRY_MS;
}

function markTableMissing(tableName) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const missingTables = getMissingTables();
    missingTables[tableName] = Date.now();
    window.localStorage.setItem(MISSING_TABLES_KEY, JSON.stringify(missingTables));
  } catch (error) {
    // Local storage is best-effort only.
  }
}

function clearTableMissing(tableName) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const missingTables = getMissingTables();
    if (!missingTables[tableName]) return;
    delete missingTables[tableName];
    window.localStorage.setItem(MISSING_TABLES_KEY, JSON.stringify(missingTables));
  } catch (error) {
    // Local storage is best-effort only.
  }
}

const defaultAnnouncements = [
  {
    id: "announcement-1",
    title: "Welcome To Fallon Church of Christ",
    body: "We are glad you are here. Service begins soon, and the pre-service loop is ready for your screens.",
    showOnLive: true,
    type: "announcement",
  },
  {
    id: "announcement-2",
    title: "Midweek Bible Study",
    body: "Join us Wednesday at 7:00 PM for Bible study and prayer.",
    showOnLive: true,
    type: "announcement",
  },
  {
    id: "announcement-3",
    title: "Prayer Requests",
    body: "Please see the welcome desk after service if you would like to share a prayer need.",
    showOnLive: true,
    type: "announcement",
  },
];

const defaultEvents = [
  {
    id: "event-1",
    title: "Community Fellowship Meal",
    body: "Bring a side if you can and stay after service.",
    eventDate: "",
    location: "Fellowship Hall",
    eventTime: "Sunday at 12:30 PM",
    showOnLive: true,
    type: "event",
  },
  {
    id: "event-2",
    title: "Youth Devotional Night",
    body: "Worship, teaching, and dinner for students.",
    eventDate: "",
    location: "Student Center",
    eventTime: "Friday at 6:30 PM",
    showOnLive: true,
    type: "event",
  },
];

function normalizeEvent(row = {}) {
  return {
    id: row.id || `event-${Date.now()}`,
    accountId: row.account_id || row.accountId || "",
    title: row.title || "",
    eventDate: row.event_date || row.eventDate || "",
    location: row.location || "",
    eventTime: row.event_time || row.eventTime || "",
    body: row.body || "",
    showOnLive: row.show_on_live !== false && row.showOnLive !== false,
    sharedWithOikosChurches: row.shared_with_oikos_churches === true || row.sharedWithOikosChurches === true,
    sharedAt: row.shared_at || row.sharedAt || "",
    sourceChurchName: row.source_church_name || row.sourceChurchName || row.organizationName || "",
    type: "event",
  };
}

function normalizeAnnouncement(row = {}) {
  return {
    id: row.id || `announcement-${Date.now()}`,
    title: row.title || "",
    body: row.body || "",
    showOnLive: row.show_on_live !== false && row.showOnLive !== false,
    type: "announcement",
    isGenerated: row.isGenerated === true || row.is_generated === true,
  };
}

function buildGroupedLoopBody(items = [], formatter) {
  const visibleItems = items.filter(Boolean);
  const shownItems = visibleItems.slice(0, MAX_PRE_SERVICE_ENTRIES);
  const extraCount = Math.max(visibleItems.length - shownItems.length, 0);

  const lines = shownItems.map((item, index) => formatter(item, index));

  if (extraCount > 0) {
    lines.push(`+ ${extraCount} more`);
  }

  return lines.join("\n\n");
}

function buildEventMeta(item = {}) {
  return [item.eventDate, item.location, item.eventTime].filter(Boolean).join(" • ");
}

function buildSharedEventMeta(item = {}) {
  return [item.sourceChurchName, item.eventDate, item.location, item.eventTime].filter(Boolean).join(" • ");
}

function parseLocalDate(value = "") {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isEventPassed(item = {}, today = new Date()) {
  const eventDate = parseLocalDate(item.eventDate || item.event_date);
  if (!eventDate) return false;

  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return eventDate < todayStart;
}

function formatMonthDay(value = "") {
  const date = parseLocalDate(value);
  if (!date) return "";

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function getYearsSinceDate(value = "", today = new Date()) {
  const date = parseLocalDate(value);
  if (!date) return "";

  let years = today.getFullYear() - date.getFullYear();
  const dateHasPassed =
    today.getMonth() > date.getMonth() ||
    (today.getMonth() === date.getMonth() && today.getDate() >= date.getDate());

  if (!dateHasPassed) {
    years -= 1;
  }

  return years > 0 ? String(years) : "";
}

function isDateInMonth(value = "", monthIndex = new Date().getMonth()) {
  const date = parseLocalDate(value);
  return Boolean(date && date.getMonth() === monthIndex);
}

function sortByMonthDay(a = {}, b = {}) {
  const aDate = parseLocalDate(a.date);
  const bDate = parseLocalDate(b.date);
  const aKey = aDate ? `${String(aDate.getMonth()).padStart(2, "0")}-${String(aDate.getDate()).padStart(2, "0")}` : "99-99";
  const bKey = bDate ? `${String(bDate.getMonth()).padStart(2, "0")}-${String(bDate.getDate()).padStart(2, "0")}` : "99-99";
  return aKey.localeCompare(bKey) || String(a.label || "").localeCompare(String(b.label || ""));
}

function buildMonthlyMilestoneAnnouncement(type, items = [], today = new Date()) {
  const monthName = today.toLocaleDateString(undefined, { month: "long" });
  const sortedItems = [...items].sort(sortByMonthDay);

  if (sortedItems.length === 0) return null;

  return {
    id: `auto-${type}-${today.getFullYear()}-${today.getMonth() + 1}`,
    title: type === "birthdays" ? `${monthName} Birthdays` : `${monthName} Anniversaries`,
    body: sortedItems
      .map((item) => {
        const dateLabel = formatMonthDay(item.date);
        const yearsLabel = item.years ? ` (${item.years} ${item.years === "1" ? "year" : "years"})` : "";
        return [dateLabel, `${item.label}${yearsLabel}`].filter(Boolean).join(" - ");
      })
      .join("\n"),
    showOnLive: true,
    type: "announcement",
    isGenerated: true,
  };
}

function prioritizeGeneratedAnnouncements(items = []) {
  const generated = [];
  const regular = [];

  items.forEach((item) => {
    if (!item) return;

    if (item.isGenerated) {
      generated.push(item);
    } else {
      regular.push(item);
    }
  });

  return [...generated, ...regular];
}

async function loadMonthlyMilestoneAnnouncements(accountId, today = new Date()) {
  if (!accountId) return [];

  const monthIndex = today.getMonth();

  try {
    let [membersResult, familiesResult] = await Promise.all([
      supabase
        .from(CHURCH_MEMBERS_TABLE)
        .select("id, first_name, last_name, birthdate, anniversary, is_active")
        .eq("account_id", accountId)
        .eq("is_active", true),
      supabase
        .from(CHURCH_FAMILIES_TABLE)
        .select("id, family_name, anniversary")
        .eq("account_id", accountId),
    ]);

    if (membersResult.error && isMissingColumnError(membersResult.error, "anniversary")) {
      membersResult = await supabase
        .from(CHURCH_MEMBERS_TABLE)
        .select("id, first_name, last_name, birthdate, is_active")
        .eq("account_id", accountId)
        .eq("is_active", true);
    }

    if (familiesResult.error && isMissingColumnError(familiesResult.error, "anniversary")) {
      familiesResult = { data: [], error: null };
    }

    if (membersResult.error || familiesResult.error) {
      const error = membersResult.error || familiesResult.error;
      if (!isMissingRelationError(error)) {
        console.error("Church monthly milestone load error:", error);
      }
      return [];
    }

    const birthdayItems = (membersResult.data || [])
      .filter((member) => isDateInMonth(member.birthdate, monthIndex))
      .map((member) => ({
        date: member.birthdate,
        label: [member.first_name, member.last_name].filter(Boolean).join(" ").trim() || "Church member",
      }));

    const anniversaryItems = [
      ...(familiesResult.data || [])
        .filter((family) => isDateInMonth(family.anniversary, monthIndex))
        .map((family) => ({
          date: family.anniversary,
          label: family.family_name || "Church family",
          years: getYearsSinceDate(family.anniversary, today),
        })),
      ...(membersResult.data || [])
        .filter((member) => isDateInMonth(member.anniversary, monthIndex))
        .map((member) => ({
          date: member.anniversary,
          label: [member.first_name, member.last_name].filter(Boolean).join(" ").trim() || "Church member",
          years: getYearsSinceDate(member.anniversary, today),
        })),
    ];

    return [
      buildMonthlyMilestoneAnnouncement("birthdays", birthdayItems, today),
      buildMonthlyMilestoneAnnouncement("anniversaries", anniversaryItems, today),
    ].filter(Boolean);
  } catch (error) {
    if (!isMissingRelationError(error)) {
      console.error("Church monthly milestone load error:", error);
    }
    return [];
  }
}

function readLocalArray(key, fallback) {
  if (typeof window === "undefined") {
    return fallback;
  }

  try {
    const raw = window.localStorage.getItem(key);

    if (!raw) {
      return fallback;
    }

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : fallback;
  } catch (error) {
    console.error("Church content local load error:", error);
    return fallback;
  }
}

function writeLocalArray(key, value) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error("Church content local save error:", error);
  }
}

export function getChurchAnnouncements() {
  return readLocalArray(ANNOUNCEMENTS_KEY, defaultAnnouncements).map((item) =>
    normalizeAnnouncement(item)
  );
}

export function getChurchEvents() {
  return readLocalArray(EVENTS_KEY, defaultEvents).map((item) => normalizeEvent(item));
}

export function saveChurchAnnouncements(items) {
  writeLocalArray(ANNOUNCEMENTS_KEY, items);
  return items;
}

export function saveChurchEvents(items) {
  writeLocalArray(EVENTS_KEY, items);
  return items;
}

async function getChurchAccountId(userId) {
  const access = await fetchOrganizationAccess(userId, "church");
  return access?.account?.id || null;
}

export async function loadChurchEvents(userId) {
  if (!userId || isTableMarkedMissing(CHURCH_EVENTS_TABLE)) {
    return getChurchEvents();
  }

  try {
    const accountId = await getChurchAccountId(userId);

    if (!accountId) {
      return getChurchEvents();
    }

    const { data, error } = await supabase
      .from(CHURCH_EVENTS_TABLE)
      .select("id, account_id, title, event_date, location, event_time, body, show_on_live, shared_with_oikos_churches, shared_at, created_at")
      .eq("account_id", accountId)
      .order("created_at", { ascending: false });

    if (error) {
      if (isMissingRelationError(error)) {
        markTableMissing(CHURCH_EVENTS_TABLE);
        return getChurchEvents();
      }
      if (isMissingColumnError(error, "shared_with_oikos_churches")) {
        const { data: legacyData, error: legacyError } = await supabase
          .from(CHURCH_EVENTS_TABLE)
          .select("id, account_id, title, event_date, location, event_time, body, show_on_live, created_at")
          .eq("account_id", accountId)
          .order("created_at", { ascending: false });

        if (!legacyError) {
          const nextEvents = Array.isArray(legacyData) ? legacyData.map((item) => normalizeEvent(item)) : [];
          saveChurchEvents(nextEvents);
          return nextEvents;
        }
      }
      console.error("Church events load error:", error);
      return getChurchEvents();
    }

    const nextEvents = Array.isArray(data) ? data.map((item) => normalizeEvent(item)) : [];
    clearTableMissing(CHURCH_EVENTS_TABLE);
    saveChurchEvents(nextEvents);
    return nextEvents;
  } catch (error) {
    if (isMissingRelationError(error)) {
      markTableMissing(CHURCH_EVENTS_TABLE);
      return getChurchEvents();
    }
    console.error("Church events load error:", error);
    return getChurchEvents();
  }
}

export async function loadSharedChurchEventsForAccount(accountId) {
  if (!accountId) return [];

  try {
    const { data, error } = await supabase.rpc("church_shared_events_for_account", {
      p_account_id: accountId,
    });

    if (error) {
      if (isMissingRelationError(error) || isMissingColumnError(error, "shared_with_oikos_churches")) {
        return [];
      }
      throw error;
    }

    return Array.isArray(data) ? data.map((item) => normalizeEvent(item)) : [];
  } catch (error) {
    console.error("Shared church events load error:", error);
    return [];
  }
}

export async function shareChurchEventWithOrganizations(user, eventId) {
  if (!eventId) {
    throw new Error("Choose an event to share.");
  }

  const sessionToken = user?.churchOrgSession?.sessionToken || user?.churchOrgSession?.session_token || "";

  if (sessionToken) {
    const { data, error } = await supabase.rpc("church_org_user_share_event_with_organizations", {
      p_session_token: sessionToken,
      p_event_id: eventId,
    });

    if (error) throw error;
    return data || {};
  }

  const { data, error } = await supabase.rpc("church_share_event_with_organizations", {
    p_event_id: eventId,
  });

  if (error) throw error;
  return data || {};
}

export async function loadChurchAnnouncements(userId) {
  if (!userId) {
    return getChurchAnnouncements();
  }

  try {
    const accountId = await getChurchAccountId(userId);

    if (!accountId) {
      return getChurchAnnouncements();
    }

    const monthlyMilestones = await loadMonthlyMilestoneAnnouncements(accountId);

    if (isTableMarkedMissing(CHURCH_ANNOUNCEMENTS_TABLE)) {
      return prioritizeGeneratedAnnouncements([
        ...getChurchAnnouncements(),
        ...monthlyMilestones,
      ]);
    }

    const { data, error } = await supabase
      .from(CHURCH_ANNOUNCEMENTS_TABLE)
      .select("id, title, body, show_on_live, created_at")
      .eq("account_id", accountId)
      .order("created_at", { ascending: false });

    if (error) {
      if (isMissingRelationError(error)) {
        markTableMissing(CHURCH_ANNOUNCEMENTS_TABLE);
        return prioritizeGeneratedAnnouncements([
          ...getChurchAnnouncements(),
          ...monthlyMilestones,
        ]);
      }
      console.error("Church announcements load error:", error);
      return prioritizeGeneratedAnnouncements([
        ...getChurchAnnouncements(),
        ...monthlyMilestones,
      ]);
    }

    const nextAnnouncements = Array.isArray(data)
      ? data.map((item) => normalizeAnnouncement(item))
      : [];
    clearTableMissing(CHURCH_ANNOUNCEMENTS_TABLE);
    saveChurchAnnouncements(nextAnnouncements);
    return prioritizeGeneratedAnnouncements([
      ...nextAnnouncements,
      ...monthlyMilestones,
    ]);
  } catch (error) {
    if (isMissingRelationError(error)) {
      markTableMissing(CHURCH_ANNOUNCEMENTS_TABLE);
      return getChurchAnnouncements();
    }
    console.error("Church announcements load error:", error);
    return getChurchAnnouncements();
  }
}

export async function createChurchAnnouncement(userId, draft) {
  const nextAnnouncement = normalizeAnnouncement(draft);

  if (!userId || isTableMarkedMissing(CHURCH_ANNOUNCEMENTS_TABLE)) {
    const nextAnnouncements = [nextAnnouncement, ...getChurchAnnouncements()];
    saveChurchAnnouncements(nextAnnouncements);
    return nextAnnouncements;
  }

  try {
    const accountId = await getChurchAccountId(userId);

    if (!accountId) {
      const nextAnnouncements = [nextAnnouncement, ...getChurchAnnouncements()];
      saveChurchAnnouncements(nextAnnouncements);
      return nextAnnouncements;
    }

    const { error } = await supabase.from(CHURCH_ANNOUNCEMENTS_TABLE).insert({
      account_id: accountId,
      title: nextAnnouncement.title,
      body: nextAnnouncement.body,
      show_on_live: nextAnnouncement.showOnLive,
      created_by: userId,
    });

    if (error) {
      throw error;
    }

    return loadChurchAnnouncements(userId);
  } catch (error) {
    if (isMissingRelationError(error)) {
      markTableMissing(CHURCH_ANNOUNCEMENTS_TABLE);
      const nextAnnouncements = [nextAnnouncement, ...getChurchAnnouncements()];
      saveChurchAnnouncements(nextAnnouncements);
      return nextAnnouncements;
    }
    console.error("Church announcement create error:", error);
    const nextAnnouncements = [nextAnnouncement, ...getChurchAnnouncements()];
    saveChurchAnnouncements(nextAnnouncements);
    return nextAnnouncements;
  }
}

export async function deleteChurchAnnouncement(userId, announcementId) {
  if (!userId || isTableMarkedMissing(CHURCH_ANNOUNCEMENTS_TABLE)) {
    const nextAnnouncements = getChurchAnnouncements().filter(
      (item) => item.id !== announcementId
    );
    saveChurchAnnouncements(nextAnnouncements);
    return nextAnnouncements;
  }

  try {
    const { error } = await supabase
      .from(CHURCH_ANNOUNCEMENTS_TABLE)
      .delete()
      .eq("id", announcementId);

    if (error) {
      throw error;
    }

    return loadChurchAnnouncements(userId);
  } catch (error) {
    if (isMissingRelationError(error)) {
      markTableMissing(CHURCH_ANNOUNCEMENTS_TABLE);
      const nextAnnouncements = getChurchAnnouncements().filter(
        (item) => item.id !== announcementId
      );
      saveChurchAnnouncements(nextAnnouncements);
      return nextAnnouncements;
    }
    console.error("Church announcement delete error:", error);
    const nextAnnouncements = getChurchAnnouncements().filter(
      (item) => item.id !== announcementId
    );
    saveChurchAnnouncements(nextAnnouncements);
    return nextAnnouncements;
  }
}

export async function updateChurchAnnouncement(userId, announcementId, draft) {
  const nextAnnouncement = normalizeAnnouncement({
    ...draft,
    id: announcementId,
  });

  if (!announcementId) {
    return getChurchAnnouncements();
  }

  if (!userId || isTableMarkedMissing(CHURCH_ANNOUNCEMENTS_TABLE)) {
    const nextAnnouncements = getChurchAnnouncements().map((item) =>
      item.id === announcementId
        ? {
            ...item,
            title: nextAnnouncement.title,
            body: nextAnnouncement.body,
            showOnLive: nextAnnouncement.showOnLive,
          }
        : item
    );
    saveChurchAnnouncements(nextAnnouncements);
    return nextAnnouncements;
  }

  try {
    const { error } = await supabase
      .from(CHURCH_ANNOUNCEMENTS_TABLE)
      .update({
        title: nextAnnouncement.title,
        body: nextAnnouncement.body,
        show_on_live: nextAnnouncement.showOnLive,
      })
      .eq("id", announcementId);

    if (error) {
      throw error;
    }

    return loadChurchAnnouncements(userId);
  } catch (error) {
    if (isMissingRelationError(error)) {
      markTableMissing(CHURCH_ANNOUNCEMENTS_TABLE);
      const nextAnnouncements = getChurchAnnouncements().map((item) =>
        item.id === announcementId
          ? {
              ...item,
              title: nextAnnouncement.title,
              body: nextAnnouncement.body,
              showOnLive: nextAnnouncement.showOnLive,
            }
          : item
      );
      saveChurchAnnouncements(nextAnnouncements);
      return nextAnnouncements;
    }
    console.error("Church announcement update error:", error);
    const nextAnnouncements = getChurchAnnouncements().map((item) =>
      item.id === announcementId
        ? {
            ...item,
            title: nextAnnouncement.title,
            body: nextAnnouncement.body,
            showOnLive: nextAnnouncement.showOnLive,
          }
        : item
    );
    saveChurchAnnouncements(nextAnnouncements);
    return nextAnnouncements;
  }
}

export async function toggleChurchAnnouncementLive(userId, announcement) {
  if (!announcement?.id) {
    return getChurchAnnouncements();
  }

  const nextValue = announcement.showOnLive === false;

  if (!userId || isTableMarkedMissing(CHURCH_ANNOUNCEMENTS_TABLE)) {
    const nextAnnouncements = getChurchAnnouncements().map((item) =>
      item.id === announcement.id
        ? {
            ...item,
            showOnLive: nextValue,
          }
        : item
    );
    saveChurchAnnouncements(nextAnnouncements);
    return nextAnnouncements;
  }

  try {
    const { error } = await supabase
      .from(CHURCH_ANNOUNCEMENTS_TABLE)
      .update({
        show_on_live: nextValue,
      })
      .eq("id", announcement.id);

    if (error) {
      throw error;
    }

    return loadChurchAnnouncements(userId);
  } catch (error) {
    if (isMissingRelationError(error)) {
      markTableMissing(CHURCH_ANNOUNCEMENTS_TABLE);
      const nextAnnouncements = getChurchAnnouncements().map((item) =>
        item.id === announcement.id
          ? {
              ...item,
              showOnLive: nextValue,
            }
          : item
      );
      saveChurchAnnouncements(nextAnnouncements);
      return nextAnnouncements;
    }
    console.error("Church announcement live toggle error:", error);
    const nextAnnouncements = getChurchAnnouncements().map((item) =>
      item.id === announcement.id
        ? {
            ...item,
            showOnLive: nextValue,
          }
        : item
    );
    saveChurchAnnouncements(nextAnnouncements);
    return nextAnnouncements;
  }
}

export async function createChurchEvent(userId, draft) {
  const nextEvent = normalizeEvent(draft);

  if (!userId || isTableMarkedMissing(CHURCH_EVENTS_TABLE)) {
    const nextEvents = [nextEvent, ...getChurchEvents()];
    saveChurchEvents(nextEvents);
    return nextEvents;
  }

  try {
    const accountId = await getChurchAccountId(userId);

    if (!accountId) {
      const nextEvents = [nextEvent, ...getChurchEvents()];
      saveChurchEvents(nextEvents);
      return nextEvents;
    }

    const { error } = await supabase.from(CHURCH_EVENTS_TABLE).insert({
      account_id: accountId,
      title: nextEvent.title,
      event_date: nextEvent.eventDate || null,
      location: nextEvent.location,
      event_time: nextEvent.eventTime,
      body: nextEvent.body,
      show_on_live: nextEvent.showOnLive,
      created_by: userId,
    });

    if (error) {
      throw error;
    }

    return loadChurchEvents(userId);
  } catch (error) {
    if (isMissingRelationError(error)) {
      markTableMissing(CHURCH_EVENTS_TABLE);
      const nextEvents = [nextEvent, ...getChurchEvents()];
      saveChurchEvents(nextEvents);
      return nextEvents;
    }
    console.error("Church event create error:", error);
    const nextEvents = [nextEvent, ...getChurchEvents()];
    saveChurchEvents(nextEvents);
    return nextEvents;
  }
}

export async function deleteChurchEvent(userId, eventId) {
  if (!userId || isTableMarkedMissing(CHURCH_EVENTS_TABLE)) {
    const nextEvents = getChurchEvents().filter((item) => item.id !== eventId);
    saveChurchEvents(nextEvents);
    return nextEvents;
  }

  try {
    const { error } = await supabase.from(CHURCH_EVENTS_TABLE).delete().eq("id", eventId);

    if (error) {
      throw error;
    }

    return loadChurchEvents(userId);
  } catch (error) {
    if (isMissingRelationError(error)) {
      markTableMissing(CHURCH_EVENTS_TABLE);
      const nextEvents = getChurchEvents().filter((item) => item.id !== eventId);
      saveChurchEvents(nextEvents);
      return nextEvents;
    }
    console.error("Church event delete error:", error);
    const nextEvents = getChurchEvents().filter((item) => item.id !== eventId);
    saveChurchEvents(nextEvents);
    return nextEvents;
  }
}

export async function updateChurchEvent(userId, eventId, draft) {
  const nextEvent = normalizeEvent({
    ...draft,
    id: eventId,
  });

  if (!eventId) {
    return getChurchEvents();
  }

  if (!userId || isTableMarkedMissing(CHURCH_EVENTS_TABLE)) {
    const nextEvents = getChurchEvents().map((item) =>
      item.id === eventId ? nextEvent : item
    );
    saveChurchEvents(nextEvents);
    return nextEvents;
  }

  try {
    const { error } = await supabase
      .from(CHURCH_EVENTS_TABLE)
      .update({
        title: nextEvent.title,
        event_date: nextEvent.eventDate || null,
        location: nextEvent.location,
        event_time: nextEvent.eventTime,
        body: nextEvent.body,
        show_on_live: nextEvent.showOnLive,
      })
      .eq("id", eventId);

    if (error) {
      throw error;
    }

    return loadChurchEvents(userId);
  } catch (error) {
    if (isMissingRelationError(error)) {
      markTableMissing(CHURCH_EVENTS_TABLE);
      const nextEvents = getChurchEvents().map((item) =>
        item.id === eventId ? nextEvent : item
      );
      saveChurchEvents(nextEvents);
      return nextEvents;
    }
    console.error("Church event update error:", error);
    const nextEvents = getChurchEvents().map((item) =>
      item.id === eventId ? nextEvent : item
    );
    saveChurchEvents(nextEvents);
    return nextEvents;
  }
}

export async function toggleChurchEventLive(userId, event) {
  if (!event?.id) {
    return getChurchEvents();
  }

  const nextValue = event.showOnLive === false;

  if (!userId || isTableMarkedMissing(CHURCH_EVENTS_TABLE)) {
    const nextEvents = getChurchEvents().map((item) =>
      item.id === event.id
        ? {
            ...item,
            showOnLive: nextValue,
          }
        : item
    );
    saveChurchEvents(nextEvents);
    return nextEvents;
  }

  try {
    const { error } = await supabase
      .from(CHURCH_EVENTS_TABLE)
      .update({
        show_on_live: nextValue,
      })
      .eq("id", event.id);

    if (error) {
      throw error;
    }

    return loadChurchEvents(userId);
  } catch (error) {
    if (isMissingRelationError(error)) {
      markTableMissing(CHURCH_EVENTS_TABLE);
      const nextEvents = getChurchEvents().map((item) =>
        item.id === event.id
          ? {
              ...item,
              showOnLive: nextValue,
            }
          : item
      );
      saveChurchEvents(nextEvents);
      return nextEvents;
    }
    console.error("Church event live toggle error:", error);
    const nextEvents = getChurchEvents().map((item) =>
      item.id === event.id
        ? {
            ...item,
            showOnLive: nextValue,
          }
        : item
    );
    saveChurchEvents(nextEvents);
    return nextEvents;
  }
}

export function getAnnouncementWidgetContent() {
  const announcements = getChurchAnnouncements();

  return {
    "recent-updates": {
      title: "Recent Updates",
      text: "See the latest announcement cards for this church mode.",
    },
    "quick-view": {
      title: "Quick View",
      text: "Open announcements fast and jump into message editing.",
    },
    "first-message": {
      title: announcements[0]?.title || "Welcome to church",
      text: announcements[0]?.body || "Current first announcement card from the app home screen.",
    },
    "second-message": {
      title: announcements[1]?.title || "More updates coming soon",
      text: announcements[1]?.body || "Current second announcement card from the app home screen.",
    },
    "open-prompt": {
      title: "Open Announcements",
      text: "Jump into the tile app to manage church updates and alerts.",
    },
  };
}

export function buildPreServiceLoopItems({
  churchName = "Fallon Church of Christ",
  churchLogoUrl = "",
  sharedEvents = [],
} = {}) {
  const announcements = getChurchAnnouncements();
  const events = getChurchEvents().filter((item) => !isEventPassed(item));
  const surroundingEventsItem = buildSurroundingChurchEventsLoopItem(sharedEvents);

  return [
    {
      id: "logo-slide",
      type: "logo",
      title: "Church Logo",
      subtitle: churchName,
      body: "",
      tone: "logo",
      showLogo: false,
      logoUrl: churchLogoUrl,
    },
    {
      id: "welcome-slide",
      type: "welcome",
      title: "Welcome To",
      subtitle: churchName,
      body: "We are glad you are here. Service will begin shortly.",
      tone: "welcome",
      showLogo: true,
      logoUrl: churchLogoUrl,
    },
    {
      id: "announcements-slide",
      type: "announcement",
      title: "Announcements",
      subtitle: "",
      body: announcements.length > 0
        ? buildGroupedLoopBody(announcements, (item, index) =>
            `${index + 1}. ${item.title}${item.body ? `\n${item.body}` : ""}`
          )
        : "No announcements have been selected yet.",
      tone: "announcement",
      entries: announcements.map((item) => ({
        id: item.id,
        title: item.title || "Church Announcement",
        body: item.body || "",
      })),
    },
    {
      id: "events-slide",
      type: "event",
      title: "Events",
      subtitle: "",
      body: events.length > 0
        ? buildGroupedLoopBody(events, (item, index) => {
            const meta = buildEventMeta(item);
            return `${index + 1}. ${item.title}${meta ? `\n${meta}` : ""}${item.body ? `\n${item.body}` : ""}`;
          })
        : "No upcoming events have been selected yet.",
      tone: "event",
      entries: events.map((item) => ({
        id: item.id,
        title: item.title || "Church Event",
        subtitle: buildEventMeta(item),
        body: item.body || "",
        eventDate: item.eventDate || "",
      })),
    },
    surroundingEventsItem,
  ].filter(Boolean);
}

export function buildSurroundingChurchEventsLoopItem(sharedEvents = []) {
  const events = (Array.isArray(sharedEvents) ? sharedEvents : [])
    .map((item) => normalizeEvent(item))
    .filter((item) => item.showOnLive !== false && !isEventPassed(item));

  if (events.length === 0) return null;

  return {
    id: "surrounding-events-slide",
    type: "event",
    title: "Surrounding Church Events",
    subtitle: "",
    body: buildGroupedLoopBody(events, (item, index) => {
      const meta = buildSharedEventMeta(item);
      return `${index + 1}. ${item.title}${meta ? `\n${meta}` : ""}${item.body ? `\n${item.body}` : ""}`;
    }),
    tone: "event",
    entries: events.map((item) => ({
      id: item.id,
      title: item.title || "Church Event",
      label: item.sourceChurchName || "",
      subtitle: buildEventMeta(item),
      body: item.body || "",
      eventDate: item.eventDate || "",
    })),
  };
}

export async function buildPreServiceLoopItemsForUser(
  userId,
  {
    churchName = "Fallon Church of Christ",
    churchLogoUrl = "",
  } = {}
) {
  const [savedAnnouncements, savedEvents] = await Promise.all([
    loadChurchAnnouncements(userId),
    loadChurchEvents(userId),
  ]);
  const accountId = await getChurchAccountId(userId).catch(() => null);
  const sharedEvents = accountId ? await loadSharedChurchEventsForAccount(accountId) : [];
  const announcements = savedAnnouncements.filter((item) => item.showOnLive !== false);
  const events = savedEvents.filter((item) => item.showOnLive !== false && !isEventPassed(item));
  const surroundingEventsItem = buildSurroundingChurchEventsLoopItem(sharedEvents);

  return [
    {
      id: "logo-slide",
      type: "logo",
      title: "Church Logo",
      subtitle: churchName,
      body: "",
      tone: "logo",
      showLogo: false,
      logoUrl: churchLogoUrl,
    },
    {
      id: "welcome-slide",
      type: "welcome",
      title: "Welcome To",
      subtitle: churchName,
      body: "We are glad you are here. Service will begin shortly.",
      tone: "welcome",
      showLogo: true,
      logoUrl: churchLogoUrl,
    },
    {
      id: "announcements-slide",
      type: "announcement",
      title: "Announcements",
      subtitle: "",
      body: announcements.length > 0
        ? buildGroupedLoopBody(announcements, (item, index) =>
            `${index + 1}. ${item.title}${item.body ? `\n${item.body}` : ""}`
          )
        : "No announcements have been selected yet.",
      tone: "announcement",
      entries: announcements.map((item) => ({
        id: item.id,
        title: item.title || "Church Announcement",
        body: item.body || "",
      })),
    },
    {
      id: "events-slide",
      type: "event",
      title: "Events",
      subtitle: "",
      body: events.length > 0
        ? buildGroupedLoopBody(events, (item, index) => {
            const meta = buildEventMeta(item);
            return `${index + 1}. ${item.title}${meta ? `\n${meta}` : ""}${item.body ? `\n${item.body}` : ""}`;
          })
        : "No upcoming events have been selected yet.",
      tone: "event",
      entries: events.map((item) => ({
        id: item.id,
        title: item.title || "Church Event",
        subtitle: buildEventMeta(item),
        body: item.body || "",
        eventDate: item.eventDate || "",
      })),
    },
    surroundingEventsItem,
  ].filter(Boolean);
}
